import React, { useEffect, useRef, useState } from 'react';
import { useStore } from '../store';
import { ArtifactBlock } from './ArtifactBlock';
import { DocHierarchyViewer, hasDocHierarchy } from './DocHierarchyViewer';
import { I } from './Shared';
import { downloadArtifactsZip } from '../lib/download';
import { buildProjectImageIndex, overlayStepArtifacts } from '../lib/docImages';
import { AttachmentBar } from './AttachmentBar';
import type { Attachment } from '../types';

export function FullscreenOverlay() {
  const project = useStore((s) => s.project);
  const open = useStore((s) => s.fsOpen);
  const stepKey = useStore((s) => s.rpStepKey);
  const idx = useStore((s) => s.fsArtifactIdx);
  const setIdx = useStore((s) => s.setFsArtifactIdx);
  const close = useStore((s) => s.closeFullscreen);
  const setPendingEdit = useStore((s) => s.setPendingEdit);
  const submitEdit = useStore((s) => s.submitEdit);
  const replaceArtifact = useStore((s) => s.replaceArtifact);
  const projectRole = useStore((s) => s.projectRole);
  const importInputRef = useRef<HTMLInputElement>(null);
  const addInputRef = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState(false);

  const step = React.useMemo(() => {
    if (!project || !stepKey) return null;
    for (const ph of project.phases) {
      const s = ph.steps.find((x) => x.key === stepKey);
      if (s) return s;
    }
    return null;
  }, [project, stepKey]);

  const [feedback, setFeedback] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  useEffect(() => {
    setFeedback(step?.pendingEdit ?? '');
    setAttachments([]);
  }, [step?.key, step?.pendingEdit]);

  const projectImageIndex = React.useMemo(
    () => (project ? buildProjectImageIndex(project) : undefined),
    [project],
  );
  const artifacts = React.useMemo(
    () => (project && step ? overlayStepArtifacts(project, step.key) : step?.artifacts ?? []),
    [project, step],
  );

  if (!open || !step) return null;
  const cur = artifacts[idx] ?? artifacts[0];
  const total = artifacts.length;
  const isDocViewer = hasDocHierarchy(artifacts);
  const canEdit = !!project?.id.startsWith('p_') && projectRole !== 'viewer';

  const handleReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !cur) return;
    setReplacing(true);
    try {
      await replaceArtifact(step.key, cur.name, await file.text());
    } catch (err: any) {
      window.alert(`Could not replace artifact: ${err?.message ?? 'unknown error'}`);
    } finally {
      setReplacing(false);
    }
  };

  // Import a file as a new artifact named after the file (upsert by name).
  const handleAddFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setReplacing(true);
    try {
      await replaceArtifact(step.key, file.name, await file.text());
    } catch (err: any) {
      window.alert(`Could not import file: ${err?.message ?? 'unknown error'}`);
    } finally {
      setReplacing(false);
    }
  };

  return (
    <div className="af fs-overlay">
      {/* header */}
      <header style={{ display: 'flex', alignItems: 'center', padding: 'var(--sp-xs) var(--sp-md)', borderBottom: '1px solid var(--line)', background: 'var(--surface)', gap: 12 }}>
        <span className="t-micro">FULLSCREEN</span>
        <span className="t-h3">{step.label}</span>
        <span className="t-micro muted">{total} artifact{total !== 1 ? 's' : ''} · v{step.version}</span>
        <span style={{ flex: 1 }} />
        {!isDocViewer && (
          <>
            <button className="btn btn--ghost btn--sm btn--icon" disabled={idx <= 0} onClick={() => setIdx(Math.max(0, idx - 1))}>{I.chevL}</button>
            <span className="t-num t-small" style={{ minWidth: 36, textAlign: 'center' }}>{Math.min(idx + 1, total)} / {total}</span>
            <button className="btn btn--ghost btn--sm btn--icon" disabled={idx >= total - 1} onClick={() => setIdx(Math.min(total - 1, idx + 1))}>{I.chevR}</button>
            <select
              className="select mono"
              style={{ width: 320, height: 30, fontSize: 12 }}
              value={String(idx)}
              onChange={(e) => setIdx(parseInt(e.target.value, 10))}
            >
              {artifacts.map((a, i) => (
                <option key={a.name + i} value={i}>{a.name}</option>
              ))}
            </select>
          </>
        )}
        <button
          className="btn btn--sm"
          disabled={total === 0}
          title={total > 1 ? 'Download all files as a zip' : 'Download file'}
          onClick={() => void downloadArtifactsZip(step.label, artifacts)}
        >
          {I.download} Download{total > 1 ? ' all' : ''}
        </button>
        {canEdit && (
          <>
            <button
              className="btn btn--sm"
              disabled={replacing}
              title="Import a file as a new artifact"
              onClick={() => addInputRef.current?.click()}
            >
              {I.upload} Import
            </button>
            <input
              ref={addInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => void handleAddFile(e)}
            />
          </>
        )}
        {canEdit && cur && (
          <>
            <button
              className="btn btn--sm"
              disabled={replacing}
              title={`Replace "${cur.name}" from a file`}
              onClick={() => importInputRef.current?.click()}
            >
              {I.upload} Replace
            </button>
            <input
              ref={importInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => void handleReplaceFile(e)}
            />
          </>
        )}
        <button className="btn btn--primary btn--sm" onClick={close}>{I.x} Exit</button>
      </header>

      {/* body */}
      {isDocViewer ? (
        <DocHierarchyViewer artifacts={artifacts} sidebarWidth={300} imageIndex={projectImageIndex} />
      ) : (
        <div className="scroll" style={{ flex: 1, padding: 'var(--sp-md)', background: 'var(--bg-sunken)' }}>
          {cur && <ArtifactBlock artifact={cur} imageIndex={projectImageIndex} />}
        </div>
      )}

      {/* feedback footer */}
      <div style={{ padding: 'var(--sp-sm) var(--sp-md)', borderTop: '1px solid var(--line)', background: 'var(--bg-sunken)' }}>
        <div className="label" style={{ marginBottom: 8 }}>Improve this step</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <textarea
            className="textarea"
            style={{ flex: 1, background: 'var(--surface)' }}
            placeholder="Describe how to improve this artifact..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={2}
          />
          <button
            className="btn btn--accent btn--sm"
            disabled={!feedback.trim() && attachments.length === 0}
            onClick={() => {
              if (project?.id.startsWith('p_')) {
                void submitEdit(step.key, feedback.trim(), attachments.length ? attachments : undefined);
                setAttachments([]);
              } else {
                setPendingEdit(step.key, feedback.trim());
              }
            }}
          >
            Improve {I.spark}
          </button>
        </div>
        <div style={{ marginTop: 8 }}>
          {/* Only real, editable projects persist attachments — disable the
              picker for viewers and demo/local projects (see ArtifactPanel). */}
          <AttachmentBar attachments={attachments} onChange={setAttachments} compact disabled={!canEdit} />
        </div>
      </div>
    </div>
  );
}
